import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle, ExternalLink, ShieldCheck, Plane, Shield } from 'lucide-react';

export const InsuranceTab = ({ insuranceData, loadingInsurance }) => {
  return (
    <div className="space-y-6">
      {loadingInsurance ? (
        <div className="flex flex-col items-center justify-center p-12 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-ocean" />
          <p className="text-ocean/60 text-sm">Generating insurance recommendations... This may take a few seconds.</p>
        </div>
      ) : insuranceData ? (
        <>
          {insuranceData.destination_risks && (
            <Card className="bg-orange-50/80 border-orange-200">
              <CardContent className="p-6">
                <p className="font-medium text-orange-800 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Destination Risk Assessment</p>
                <p className="text-orange-700 text-sm">{insuranceData.destination_risks}</p>
              </CardContent>
            </Card>
          )}
          {insuranceData.essential_coverage && (
            <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
              <CardHeader><CardTitle className="font-serif text-lg text-ocean">Essential Coverage</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {insuranceData.essential_coverage.map((item, i) => (
                    <span key={`coverage-${item.slice(0,20)}-${i}`} className="px-3 py-1.5 bg-ocean/10 text-ocean rounded-full text-sm font-medium">{item}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {insuranceData.recommendations && (
            <div className="grid md:grid-cols-2 gap-4">
              {insuranceData.recommendations.map((rec, i) => (
                <Card key={`rec-${rec.provider}-${rec.plan_name}-${i}`} className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl hover:shadow-2xl transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-ocean text-lg">{rec.provider}</h4>
                        <p className="text-ocean/60 text-sm">{rec.plan_name}</p>
                      </div>
                      <span className="bg-gold/10 text-gold-dark px-3 py-1 rounded-full text-sm font-bold">{rec.estimated_cost}</span>
                    </div>
                    {rec.rating && <p className="text-burnt text-sm mb-3">Rating: {rec.rating}</p>}
                    {rec.coverage_highlights && (
                      <ul className="space-y-1 mb-4">
                        {rec.coverage_highlights.map((h, j) => (
                          <li key={`highlight-${h.slice(0,20)}-${j}`} className="text-sm text-charcoal flex gap-2"><span className="text-green-500">&#10003;</span>{h}</li>
                        ))}
                      </ul>
                    )}
                    {rec.website && (
                      <a href={rec.website} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-ocean hover:underline">
                        Visit Website <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {insuranceData.tips && (
            <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
              <CardHeader><CardTitle className="font-serif text-lg text-ocean">Insurance Tips</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {insuranceData.tips.map((tip, i) => (
                    <li key={`tip-${tip.slice(0,20)}-${i}`} className="text-sm text-charcoal flex gap-2"><span className="text-gold">&#9733;</span>{tip}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <div className="text-center p-12 text-ocean/50"><p>Click the Insurance tab to load recommendations</p></div>
      )}

      {/* Affiliate Insurance & Compensation Services - Always visible */}
      <h3 className="font-serif text-lg text-white drop-shadow-md flex items-center gap-2 pt-2">
        <ShieldCheck className="w-5 h-5 text-amber-400" /> Book Insurance & Compensation
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="insurance-affiliate-services">
        <a href="https://ektatraveling.tpm.lv/eZ9zv9vF" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-amber-500/20 p-5 hover:shadow-lg transition-all hover:border-amber-500/40" data-testid="ekta-insurance-link">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Ekta Travel Insurance</h4>
              <p className="text-charcoal/60 text-xs mb-2">Comprehensive coverage for medical, trip cancellation, and luggage. Instant policy.</p>
              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold text-xs group-hover:text-amber-700">
                Get Covered <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://airhelp.tpm.lv/PWwYaq5O" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-red-500/20 p-5 hover:shadow-lg transition-all hover:border-red-500/40" data-testid="airhelp-insurance-link">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center flex-shrink-0">
              <Plane className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">AirHelp</h4>
              <p className="text-charcoal/60 text-xs mb-2">Flight delayed or cancelled? Claim up to $600 compensation. No win, no fee.</p>
              <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-xs group-hover:text-red-700">
                Check Eligibility <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://compensair.tpm.lv/ZOWHHas3" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-purple-500/20 p-5 hover:shadow-lg transition-all hover:border-purple-500/40" data-testid="compensair-insurance-link">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Compensair</h4>
              <p className="text-charcoal/60 text-xs mb-2">Get money back for flight disruptions. Expert legal support, pay only if you win.</p>
              <span className="inline-flex items-center gap-1 text-purple-600 font-semibold text-xs group-hover:text-purple-700">
                Claim Now <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};
